"use client";
import { useQuery } from "@apollo/client/react";
import { useEffect, useRef } from "react";

import { graphql } from "../../__generated__/gql";
import type { StripeCard } from "../../__generated__/gql/graphql";
import { useGtm } from "../../lib/google-tag-manager/gtm";

import { CreditcardInfo } from "./creditcard-info/creditcard-info";
import type { CreditcardInfoLayoutType } from "./creditcard-info/creditcard-info";
import type { CreditcardInfoMessages } from "./creditcard-info/creditcard-info";

const Query = graphql(`
  query GetUserCard {
    maybeViewer {
      stripe {
        defaultCard {
          brand
          last4
          exp_month
          exp_year
        }
      }
    }
  }
`);

export type UserStripeCardInfoMessages = {
  loadingCardInfo: string;
  creditcardInfo?: CreditcardInfoMessages;
};

const JA_MESSAGES: UserStripeCardInfoMessages = {
  loadingCardInfo: "カード情報読み込み中...",
};

// 各ページ埋め込み用のユーザーカード情報の表示と入力を促すコンポーネント
export function UserStripeCardInfo({
  onCardChange,
  variant,
  messages = JA_MESSAGES,
}: {
  onCardChange?: (card: StripeCard | null) => void;
  variant?: CreditcardInfoLayoutType;
  messages?: UserStripeCardInfoMessages;
}) {
  const { data, loading } = useQuery(Query);
  const card = data?.maybeViewer?.stripe.defaultCard;
  const gtm = useGtm();
  const sentCardExistingRef = useRef(false);

  useEffect(() => {
    onCardChange?.(card ?? null);
  }, [card, onCardChange]);

  useEffect(() => {
    if (loading || sentCardExistingRef.current) return;
    sentCardExistingRef.current = true;
    if (card) {
      gtm.sendEvent("card_existing");
      gtm.sendEvent("card_ready");
    }
  }, [card, loading, gtm]);

  if (!loading && !data?.maybeViewer) {
    return;
  }

  return (
    <div className="bg-white">
      {loading ? (
        <div className="p-[30px] text-[12px]">{messages.loadingCardInfo}</div>
      ) : (
        <CreditcardInfo
          card={card}
          variant={variant}
          messages={messages.creditcardInfo}
        />
      )}
    </div>
  );
}
